import { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { clsx } from 'clsx';

/**
 * Stat Card Component
 */
export function StatCard({ 
  title, 
  value, 
  change, 
  changeLabel, 
  icon: Icon, 
  iconColor = 'text-accent-primary',
  loading = false 
}) {
  const isPositive = change > 0;
  const isNegative = change < 0;

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-start justify-between">
          <div className="skeleton w-24 h-4 mb-3" />
          <div className="skeleton w-10 h-10 rounded-lg" />
        </div>
        <div className="skeleton w-32 h-8 mb-2" />
        <div className="skeleton w-20 h-4" />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <p className="text-sm text-gray-400">{title}</p>
        {Icon && (
          <div className={clsx('p-2 rounded-lg bg-dark-600', iconColor)}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      <p className="stat-value mt-2">{value}</p>
      {change !== undefined && (
        <div className={clsx(
          'stat-change',
          isPositive && 'stat-change-up',
          isNegative && 'stat-change-down',
          !isPositive && !isNegative && 'text-gray-400'
        )}>
          {isPositive && <TrendingUp className="w-3 h-3" />}
          {isNegative && <TrendingDown className="w-3 h-3" />}
          {!isPositive && !isNegative && <Minus className="w-3 h-3" />}
          <span>{isPositive && '+'}{change}%</span>
          {changeLabel && <span className="text-gray-500 ml-1">{changeLabel}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * Badge Component
 */
export function Badge({ variant = 'neutral', children, className }) {
  const variants = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
  };

  return (
    <span className={clsx(variants[variant], className)}>
      {children}
    </span>
  );
}

/**
 * Status Badge Component
 */
export function StatusBadge({ status }) {
  const statusConfig = {
    healthy: { variant: 'success', label: 'Healthy' },
    warning: { variant: 'warning', label: 'Warning' },
    critical: { variant: 'danger', label: 'Critical' },
    paused: { variant: 'neutral', label: 'Paused' },
    success: { variant: 'success', label: 'Success' },
    failed: { variant: 'danger', label: 'Failed' },
    skipped: { variant: 'warning', label: 'Skipped' },
    pending: { variant: 'info', label: 'Pending' },
  };

  const config = statusConfig[status] || { variant: 'neutral', label: status };

  return (
    <Badge variant={config.variant}>
      <span className="relative flex h-2 w-2 mr-1.5">
        <span className={clsx(
          'relative inline-flex rounded-full h-2 w-2',
          config.variant === 'success' && 'bg-emerald-400',
          config.variant === 'warning' && 'bg-amber-400',
          config.variant === 'danger' && 'bg-red-400',
          config.variant === 'info' && 'bg-accent-primary',
          config.variant === 'neutral' && 'bg-gray-400'
        )} />
      </span>
      {config.label}
    </Badge>
  );
}

/**
 * Progress Bar Component
 */
export function ProgressBar({ 
  value, 
  max = 100, 
  color = 'bg-accent-primary', 
  showLabel = true,
  size = 'md' 
}) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  
  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  };

  return (
    <div className="w-full">
      <div className={clsx('progress-bar', sizeClasses[size])}>
        <div 
          className={clsx('progress-bar-fill', color)}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <div className="flex justify-between mt-1 text-xs text-gray-400">
          <span>{value}</span>
          <span>{max}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Loading Spinner Component
 */
export function Spinner({ size = 'md', className }) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <svg 
      className={clsx('animate-spin', sizeClasses[size], className)} 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Empty State Component
 */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-12">
      {Icon && (
        <div className="mx-auto w-12 h-12 rounded-full bg-dark-600 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-gray-400" />
        </div>
      )}
      <h3 className="text-lg font-medium text-white mb-1">{title}</h3>
      {description && (
        <p className="text-gray-400 mb-4">{description}</p>
      )}
      {action}
    </div>
  );
}

/**
 * Card Component
 */
export function Card({ children, className, hover = false }) {
  return (
    <div className={clsx(hover ? 'card-hover' : 'card', className)}>
      {children}
    </div>
  );
}

/**
 * Card Header Component
 */
export function CardHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-lg font-semibold text-accent-primary">{title}</h3>
        {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * InfoTip — small `?` icon next to a label that reveals a one-sentence
 * explanation on hover (desktop) or tap (mobile). Use it for field-level
 * help next to form labels, hero-metric labels, or anywhere a brand-new user
 * might wonder "what is this?". Keep the body to ~25 words max — link to
 * /help-center or /learning-library for anything longer.
 */
export function InfoTip({ children, side = 'top', iconClass = 'w-3 h-3 text-gray-500 hover:text-amber-400' }) {
  const [open, setOpen] = useState(false);
  const positions = {
    top:    'bottom-full mb-1.5 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-1.5 left-1/2 -translate-x-1/2',
    left:   'right-full mr-1.5 top-1/2 -translate-y-1/2',
    right:  'left-full ml-1.5 top-1/2 -translate-y-1/2',
  };
  return (
    <span
      className="relative inline-flex items-center align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
    >
      <Info className={clsx('cursor-help transition-colors', iconClass)} />
      {open && (
        <span
          role="tooltip"
          className={clsx(
            'absolute z-50 w-60 px-3 py-2 rounded-lg text-[11px] leading-relaxed text-gray-200',
            'bg-dark-900 border border-dark-600 shadow-xl pointer-events-none normal-case font-normal text-left',
            positions[side] || positions.top
          )}
        >
          {children}
        </span>
      )}
    </span>
  );
}
